import moreIcon from '@disy/cadenza-icons/more.svg';

import { MENU_ITEM_SEPARATOR, setupMenuButton } from 'ui/menu/menu';
import { createIconButton } from 'ui/button/button';

import { icon } from 'cadenza/utils/icon/icon';
import { commonI18n as i18n } from 'cadenza/utils/i18n/i18n';
import { logger } from 'cadenza/utils/logging';
import { Action } from 'cadenza/action';

const DEFAULT_BUTTON_WIDTH = 30;
const BUTTON_MARGIN = 4;

// Please do NOT add random indexes/timeout values such as 1, 2, 5 in this file.
// If you do, add a comment explaining the chosen value

/**
 * Create a menu button and append it to the toolbar.
 *
 * @param {HTMLElement} toolbar - The toolbar which will contain the menu button
 * @param {object} toolbarStructure - Datastructure containing the items which will be displayed in the menu
 * @param {number} endIndex - Buttons before and inclusing this index do not get shown in the menu.
 */
export function appendOverflowDropdownButton (toolbar, toolbarStructure, endIndex = 0) {
  const menuButton = createMenuButton(toolbarStructure, endIndex);
  // Do not use the toolbar.addButtons, since it may add an extra separator at the end,
  // which will be displayed as the last one in the menu and looks weird
  toolbar.append(menuButton);
  initDropDownMenuAndCreateResizeObserver(toolbar, menuButton, endIndex);
}

function initDropDownMenuAndCreateResizeObserver (parent, menuButton, endIndex) {
  adjustDropdownMenu(parent, menuButton, endIndex);
  const resizeObserver = new ResizeObserver(([ toolbarTargetEntry ]) => {
    // This has to run after the gazetteer has resized while crossing the Sm breakpoint --> setTimeout
    setTimeout(() => adjustDropdownMenu(toolbarTargetEntry.target, menuButton, endIndex), 5);
  });
  resizeObserver.observe(parent);
  return resizeObserver;
}

function createMenuButton (toolbarStructure, endIndex) {
  const button = createIconButton(icon(moreIcon), i18n('common:moreActions'), {
    variant: 'borderless',
    styleClass: 'more-actions'
  });

  const contentProvider = () => {
    const fromIndex = Math.max(button.dataset.fromIndex, endIndex);
    return getVisibleAndEnabledMenuItems(toolbarStructure.getValues(), fromIndex)
      .then(items => {
        return items
          .filter(item => item != null)
          .map(item => {
            if (item.action) {
              return item.group
                ? createGroupItem(item.action, item.group)
                : createItem(item.action);
            }
            return item;
          });
      });
  };

  setupMenuButton(button, contentProvider);
  return button;
}

function getVisibleAndEnabledMenuItems (items, fromIndex) {
  return Promise.all(items
    .slice(fromIndex)
    .map(item => typeof item === 'function' ? item() : item)
    .filter(item => !item.hidden)
    .map(getSupportedItemPromise));
}

function getSupportedItemPromise (item) {
  if (item.group) {
    return Promise.all(item.group.map(groupItem => groupItem instanceof Action ? getActionPromise(groupItem) : groupItem))
      .then(groupItems => groupItems.some(Boolean) ? { ...item, group: groupItems } : null);
  }
  if (item.action) {
    return getActionPromise(item.action, item);
  }
  return item;
}

function getActionPromise (action, successValue = action) {
  return action.isSupported()
    .then(isSupported => isSupported ? successValue : null)
    .catch(error => {
      logger.error('Error while evaluating action supported', error);
      return null;
    });
}

function adjustDropdownMenu (toolbar, menuButton, endIndex) {
  if (toolbar.hidden || !toolbar.parentElement) {
    return;
  }
  const lastPossiblyVisibleElementIndex = toolbar.children.length - 2; // at -1 is the menu
  const toolbarBoundingClientRect = toolbar.getBoundingClientRect();
  // When called for the first time we want to default to the toolbar shrinking, in case we need to hide some toolbar buttons
  const isInitialAdjustment = toolbar.lastToolbarWidth === 0;
  const isGrowing = toolbar.lastToolbarWidth < toolbarBoundingClientRect.width;
  const buttonWidth = menuButton.clientWidth > 0 ? menuButton.clientWidth + BUTTON_MARGIN : DEFAULT_BUTTON_WIDTH + BUTTON_MARGIN;
  let lastVisibleButtonIndex = getLastVisibleToolbarBtn(toolbar.children, toolbarBoundingClientRect.right, isGrowing, buttonWidth, lastPossiblyVisibleElementIndex);
  if (lastVisibleButtonIndex !== -1) {
    // logger.debug(`IsGrowing: ${isGrowing}. Last visible button index: ${lastVisibleButtonIndex}`);
    if (lastVisibleButtonIndex <= endIndex) {
      lastVisibleButtonIndex = endIndex; // Don't collapse the gazetteer, it would look strange
    }

    if (isGrowing && !isInitialAdjustment) {
      showButtons(Array.from(toolbar.children), lastVisibleButtonIndex);
    } else {
      hideButtons(Array.from(toolbar.children), lastVisibleButtonIndex + 1, lastPossiblyVisibleElementIndex); // Start hiding from the next button
    }
  }

  menuButton.dataset.fromIndex = lastVisibleButtonIndex + 1;
  const isMenuHidden = isMenuButtonHidden(lastVisibleButtonIndex, lastPossiblyVisibleElementIndex);
  menuButton.hidden = isMenuHidden;
  toolbar.classList.toggle('with-dropdown', !isMenuHidden);
  toolbar.lastToolbarWidth = toolbarBoundingClientRect.width;
}

function getLastVisibleToolbarBtn (toolbarChildren, toolbarRightPosition, isGrowing, buttonWidth, lastPossiblyVisibleElement) {
  let lastVisibleIndexExceptMenu = -1;
  if (isChildInView(toolbarChildren[lastPossiblyVisibleElement], toolbarRightPosition, buttonWidth)) {
    return lastVisibleIndexExceptMenu; // There is enough space for buttons
  }

  if (isGrowing) {
    lastVisibleIndexExceptMenu = findLastVisibleIndex(toolbarChildren, child => !child.hidden);
    const menuButton = toolbarChildren[toolbarChildren.length - 1];
    const extraSpace = toolbarRightPosition - menuButton.getBoundingClientRect().right;
    return Math.min(lastPossiblyVisibleElement, lastVisibleIndexExceptMenu + Math.floor(extraSpace / buttonWidth));
  } else {
    lastVisibleIndexExceptMenu = findLastVisibleIndex(toolbarChildren, child => isChildInView(child, toolbarRightPosition, buttonWidth));
    return Math.min(lastPossiblyVisibleElement, lastVisibleIndexExceptMenu);
  }
}

function findLastVisibleIndex (toolbarChildren, isVisiblePredicate) {
  // Starting at length - 2, because the menu is at length - 1, and it's visible.
  // Starting from the end also solves issues with skipping the legend button whose hidden flag is toggled manually by the legend panel
  for (let i = toolbarChildren.length - 2; i > 0; i--) {
    if (isVisiblePredicate(toolbarChildren[i])) {
      return i;
    }
  }
  return -1;
}

function isChildInView (childElement, toolbarRightPosition, buttonWidth) {
  const currentChildSize = childElement.getBoundingClientRect();
  return !childElement.hidden && currentChildSize.right + buttonWidth < toolbarRightPosition;
}

function hideButtons (elements, firstElementToHideIndex, lastPossiblyVisibleElementIndex) {
  if (firstElementToHideIndex <= lastPossiblyVisibleElementIndex) {
    for (let i = firstElementToHideIndex; i <= lastPossiblyVisibleElementIndex; i++) {
      elements[i].hidden = true;
    }
  }
}

function showButtons (elements, lastVisibleIndex) {
  if (lastVisibleIndex < elements.length) {
    // Start from the end and stop at first visible element, assuming that we needn't show elements before that element,
    // because we didn't hide them, not using the logic in this file.
    for (let i = lastVisibleIndex; i > 0; i--) {
      if (!elements[i].hidden) {
        break;
      }
      elements[i].hidden = false;
    }
  }
}

function createItem ({ label, icon: iconParam, execute } = {}) {
  return { label, icon: iconParam, execute };
}

function createGroupItem (action, group) {
  return {
    icon: action.icon,
    label: action.label,
    subMenu: () => group
      .filter(item => item != null)
      .map(item => item === MENU_ITEM_SEPARATOR ? item : createItem(item))
  };
}

function isMenuButtonHidden (lastVisibleButtonIndex, lastPossiblyVisibleElementIndex) {
  return lastVisibleButtonIndex === -1 || lastVisibleButtonIndex === lastPossiblyVisibleElementIndex;
}
